import { useState, useEffect, useRef } from 'react';
import { Eye, ChevronDown, Search, X, Building2 } from 'lucide-react';
import { useStudioStore } from '../../stores/studioStore';
import type { School, SchoolBrand } from '../../types/index';

export default function EditAsSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const schools = useStudioStore(s => s.schools);
  const schoolBrands = useStudioStore(s => s.schoolBrands);
  const schoolsLoading = useStudioStore(s => s.schoolsLoading);
  const editAsBrandId = useStudioStore(s => s.editAsBrandId);
  const editAsLoading = useStudioStore(s => s.editAsLoading);
  const setEditAsBrand = useStudioStore(s => s.setEditAsBrand);
  const loadSchools = useStudioStore(s => s.loadSchools);
  const loadSchoolBrands = useStudioStore(s => s.loadSchoolBrands);

  useEffect(() => {
    if (isOpen && schools.length === 0 && !schoolsLoading) {
      loadSchools();
      loadSchoolBrands();
    }
  }, [isOpen, schools.length, schoolsLoading, loadSchools, loadSchoolBrands]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const schoolMap = new Map<string, { school: School; brands: SchoolBrand[] }>();
  for (const school of schools) {
    schoolMap.set(school.id, { school, brands: [] });
  }
  for (const brand of schoolBrands) {
    const entry = schoolMap.get(brand.school_id);
    if (entry) {
      entry.brands.push(brand);
    }
  }

  const filtered = Array.from(schoolMap.values()).filter(({ school, brands }) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      (school.name || '').toLowerCase().includes(q) ||
      brands.some(b =>
        (b.school_name || '').toLowerCase().includes(q)
      )
    );
  });

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
          editAsBrandId
            ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
            : 'bg-white/[0.04] text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.08] border border-transparent'
        }`}
      >
        <Eye size={14} />
        <span className="hidden sm:inline">{editAsBrandId ? 'Previewing' : 'Edit As'}</span>
        <ChevronDown size={12} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-1 w-80 bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search schools or brands..."
                className="w-full pl-8 pr-3 py-1.5 bg-white/5 border border-white/10 rounded text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-500/50"
                autoFocus
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-white/30 hover:text-white"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {editAsBrandId && (
            <button
              onClick={() => {
                setEditAsBrand(null);
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-400 hover:bg-white/5 border-b border-white/10"
            >
              <X size={14} />
              Clear Preview — Return to Template
            </button>
          )}

          <div className="max-h-64 overflow-y-auto sidebar-scroll">
            {schoolsLoading ? (
              <div className="p-4 text-center text-sm text-white/40">Loading schools...</div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-center text-sm text-white/40">
                {search ? 'No matches found' : 'No schools available'}
              </div>
            ) : (
              filtered.map(({ school, brands }) => (
                <div key={school.id}>
                  <div className="px-3 py-1.5 text-xs font-medium text-white/40 uppercase tracking-wider bg-white/[0.02]">
                    <div className="flex items-center gap-1.5">
                      <Building2 size={12} />
                      {school.name}
                    </div>
                  </div>

                  {brands.length > 0 ? (
                    brands.map((brand) => (
                      <button
                        key={brand.id}
                        onClick={() => {
                          setEditAsBrand(brand.id);
                          setIsOpen(false);
                        }}
                        disabled={editAsLoading}
                        className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${
                          brand.id === editAsBrandId
                            ? 'bg-cyan-500/10 text-cyan-400'
                            : 'text-white/70 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <div className="flex gap-1">
                          {brand.rgb_primary && (
                            <div
                              className="w-3 h-3 rounded-full border border-white/20"
                              style={{ backgroundColor: brand.rgb_primary }}
                            />
                          )}
                          {brand.rgb_secondary && (
                            <div
                              className="w-3 h-3 rounded-full border border-white/20"
                              style={{ backgroundColor: brand.rgb_secondary }}
                            />
                          )}
                        </div>
                        <span>{brand.school_name || 'Default Brand'}</span>
                        {brand.id === editAsBrandId && (
                          <Eye size={12} className="ml-auto text-cyan-400" />
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-xs text-white/30 italic">
                      No brands configured
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
